import React, { useState } from 'react';
import './login-screen.scss';
import { config } from '../../config/runtime-config';

export interface UserData {
  userId: string;
  firstName: string;
  lastName: string;
  phone: string;
  language: string;
}

interface LoginScreenProps {
  onComplete: (userData: UserData) => void;
}

const LABELS = {
  en: {
    title: 'Dubai Police',
    subtitle: '999 Emergency Assistant',
    firstName: 'First Name',
    lastName: 'Last Name',
    phone: 'Phone Number',
    language: 'Preferred Language',
    submit: 'Get Started',
    submitting: 'Please wait…',
    errors: {
      firstName: 'Please enter your first name',
      lastName: 'Please enter your last name',
      phone: 'Please enter a valid phone number',
    },
  },
  ar: {
    title: 'شرطة دبي',
    subtitle: 'مساعد الطوارئ 999',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    phone: 'رقم الهاتف',
    language: 'اللغة المفضلة',
    submit: 'ابدأ',
    submitting: 'الرجاء الانتظار…',
    errors: {
      firstName: 'يرجى إدخال اسمك الأول',
      lastName: 'يرجى إدخال اسم عائلتك',
      phone: 'يرجى إدخال رقم هاتف صالح',
    },
  },
  ur: {
    title: 'دبئی پولیس',
    subtitle: '999 ایمرجنسی اسسٹنٹ',
    firstName: 'پہلا نام',
    lastName: 'خاندانی نام',
    phone: 'فون نمبر',
    language: 'پسندیدہ زبان',
    submit: 'شروع کریں',
    submitting: 'براہ کرم انتظار کریں…',
    errors: {
      firstName: 'براہ کرم اپنا پہلا نام درج کریں',
      lastName: 'براہ کرم اپنا خاندانی نام درج کریں',
      phone: 'براہ کرم ایک درست فون نمبر درج کریں',
    },
  },
};

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
  { code: 'ur', label: 'اردو' },
];

const LoginScreen: React.FC<LoginScreenProps> = ({ onComplete }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [language, setLanguage] = useState('en');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const t = LABELS[language as keyof typeof LABELS] || LABELS.en;

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!firstName.trim()) newErrors.firstName = t.errors.firstName;
    if (!lastName.trim()) newErrors.lastName = t.errors.lastName;
    if (!phone.trim() || !/^\+?[\d\s\-()]{7,15}$/.test(phone.trim())) {
      newErrors.phone = t.errors.phone;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const backendUrl = config.REACT_APP_BACKEND_URL;
      let userId = `local_${Date.now()}`;

      if (backendUrl) {
        try {
          const res = await fetch(`${backendUrl}/api/user/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              phone: phone.trim(),
              preferred_language: language,
            }),
          });
          if (res.ok) {
            const data = await res.json();
            userId = data.user?.id || userId;
          }
        } catch (err) {
          console.warn('[LoginScreen] Backend registration failed, continuing offline:', err);
        }
      }

      const userData: UserData = {
        userId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        language,
      };

      // Persist to localStorage
      localStorage.setItem('dp_user_data', JSON.stringify(userData));

      onComplete(userData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-container">
        {/* Header */}
        <div className="login-header">
          <img src="/logo.png" alt="Dubai Police " className="login-emblem" />
          {/*<h1 className="login-title">{t.title}</h1>*/}
          {/*<p className="login-subtitle">{t.subtitle}</p>*/}
        </div>

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          <div className="form-row two-col">
            <div className="form-group">
              <label htmlFor="firstName">{t.firstName}</label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={errors.firstName ? 'error' : ''}
                autoComplete="given-name"
              />
              {errors.firstName && <span className="field-error">{errors.firstName}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="lastName">{t.lastName}</label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={errors.lastName ? 'error' : ''}
                autoComplete="family-name"
              />
              {errors.lastName && <span className="field-error">{errors.lastName}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="phone">{t.phone}</label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={errors.phone ? 'error' : ''}
              autoComplete="tel"
              placeholder="+971 50 000 0000"
            />
            {errors.phone && <span className="field-error">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="language">{t.language}</label>
            <select
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className="btn-primary submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? t.submitting : t.submit}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginScreen;
